/**
 * Início do Portal Operacional — o que o operador faz agora e o que está pendente.
 *
 * Números que dependem de fonte ainda inexistente (plano preventivo, data de
 * inspeção de pneu) aparecem como "—" com o motivo, nunca como zero.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceAvailabilityDto, MaintenancePortalHomeDto } from "@bi/types";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtNumber } from "../../lib/format";
import { KPIStatCard } from "../../components/KPIStatCard";
import {
  IconChevronRight, IconClipboard, IconShield, IconTire, IconWrench,
} from "../../components/icons";
import { KIND_LABEL } from "../../features/maintenance/labels";
import { useCapabilities } from "../../features/maintenance/useCapabilities";
import { portalPaths } from "../../features/portal/portal-nav";

const valor = (a: MaintenanceAvailabilityDto<number> | undefined) => (a?.available ? fmtNumber(a.value) : "—");
const motivo = (a: MaintenanceAvailabilityDto<number> | undefined) => (a && !a.available ? a.reason : undefined);

export function PortalHomePage() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { data: h, isLoading } = useQuery({
    queryKey: ["maintenance", "portal", "home"],
    queryFn: () => api.get<MaintenancePortalHomeDto>("/maintenance/portal/home"),
    refetchOnWindowFocus: true,
  });

  const primeiroNome = user?.name?.split(" ")[0] ?? "";
  const hoje = new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", timeZone: "America/Sao_Paulo" });
  const executa = can("checklist.execute");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Olá{primeiroNome ? `, ${primeiroNome}` : ""}</h1>
        <p className="text-sm text-gray-500 first-letter:uppercase">{hoje}</p>
      </div>

      {executa && (
        <Link to={portalPaths.newChecklist()} className="btn-primary w-full justify-center min-h-[52px] text-base">
          <IconClipboard className="w-5 h-5" />
          Iniciar Checklist de Frota
        </Link>
      )}
      {executa && (
        <div className="grid grid-cols-2 gap-2 -mt-2">
          <Link to={portalPaths.newChecklist("diario")} className="btn-secondary justify-center min-h-[48px]">
            Checklist Diário
          </Link>
          <Link to={portalPaths.newChecklist("manutencao")} className="btn-secondary justify-center min-h-[48px]">
            Completo e outros
          </Link>
        </div>
      )}

      {/* Continuar */}
      {(h?.myDrafts.length ?? 0) > 0 && (
        <section className="card divide-y divide-surface-border">
          <h2 className="px-4 py-3 text-sm font-semibold text-gray-900">Continuar checklist</h2>
          {h!.myDrafts.map((d) => (
            <Link key={d.id} to={portalPaths.run(d.id)} className="flex items-center gap-3 px-4 py-3 min-h-[56px] hover:bg-surface-muted">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{d.templateName ?? KIND_LABEL[d.kind]}</p>
                <p className="text-xs text-gray-500 truncate">
                  <span className="font-mono">{d.vehiclePlate}</span>
                  {d.trailerPlate && <span className="font-mono"> + {d.trailerPlate}</span>}
                  {" · "}{d.answered} respondido(s) · {new Date(d.startedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                </p>
              </div>
              <span className="badge-amber">Em andamento</span>
              <IconChevronRight className="w-4 h-4 text-gray-400" />
            </Link>
          ))}
        </section>
      )}

      {/* Ações rápidas */}
      <section className="grid grid-cols-2 gap-3">
        {[
          { to: portalPaths.maintenance, label: "Checklist de Manutenção", icon: IconWrench },
          { to: portalPaths.occurrences, label: "Não Conformidades", icon: IconShield },
          { to: `${portalPaths.maintenance}#ordens`, label: "Manutenções", icon: IconWrench },
          { to: portalPaths.tires, label: "Pneus", icon: IconTire },
        ].map((a) => (
          <Link key={a.label} to={a.to} className="card p-4 min-h-[72px] flex items-center gap-3 hover:shadow-card-md transition-shadow">
            <div className="w-9 h-9 rounded-lg bg-tm-50 text-tm-600 grid place-items-center flex-shrink-0">
              <a.icon className="w-5 h-5" />
            </div>
            <span className="text-sm font-medium text-gray-800 leading-tight">{a.label}</span>
          </Link>
        ))}
      </section>

      {/* Situação */}
      <section className="grid grid-cols-2 gap-3">
        <KPIStatCard label="Checklists pendentes" memory={{ cardKey: "manutencao.checklists-pendentes", expected: h?.runsInProgress }} value={h ? fmtNumber(h.runsInProgress) : "—"}
          subtitle="em andamento na empresa" loading={isLoading}
          status={h && h.runsInProgress > 0 ? "warning" : "neutral"} />
        <KPIStatCard label="Não conformidades" memory={{ cardKey: "manutencao.nc-abertas", expected: h?.nonConformitiesOpen }} value={h ? fmtNumber(h.nonConformitiesOpen) : "—"}
          subtitle={h ? `${fmtNumber(h.nonConformitiesCritical)} crítica(s) abertas` : undefined} loading={isLoading}
          status={h ? (h.nonConformitiesCritical > 0 ? "critical" : h.nonConformitiesOpen > 0 ? "warning" : "good") : "neutral"} />
        <KPIStatCard label="Manutenções vencidas" memory={{ cardKey: "manutencao.os-vencidas", expected: h?.workOrdersOverdue }} value={h ? fmtNumber(h.workOrdersOverdue) : "—"}
          subtitle={h ? `O.S. com prazo vencido · ${fmtNumber(h.workOrdersOpen)} abertas` : undefined} loading={isLoading}
          insight={motivo(h?.preventiveOverdue)}
          status={h && h.workOrdersOverdue > 0 ? "critical" : "neutral"} />
        <KPIStatCard label="Inspeções de pneu atrasadas" memory={{ cardKey: "manutencao.inspecoes-pneu" }} value={valor(h?.inspectionsOverdue)}
          insight={motivo(h?.inspectionsOverdue)} loading={isLoading} status="neutral" />
        <KPIStatCard label="Alertas críticos hoje" memory={{ cardKey: "manutencao.criticas-hoje", expected: h?.criticalToday }} value={h ? fmtNumber(h.criticalToday) : "—"}
          subtitle="ocorrências de criticidade alta abertas hoje" loading={isLoading}
          status={h && h.criticalToday > 0 ? "critical" : "neutral"} />
        <KPIStatCard label="Pneus críticos" memory={{ cardKey: "manutencao.pneus-criticos", expected: h?.tiresCritical.available ? h.tiresCritical.value : null }} value={valor(h?.tiresCritical)}
          subtitle="abaixo da milimetragem de segurança" loading={isLoading}
          action={<Link to={portalPaths.tires} className="text-xs text-tm-700 hover:underline">Ver pneus</Link>}
          status={h?.tiresCritical.available && h.tiresCritical.value > 0 ? "warning" : "neutral"} />
      </section>
    </div>
  );
}
